import { useMemo } from "react";
import type { IconColor, IconName, Omittable } from "@salestrak/ui";

import { AGE_STATUS_CONFIG } from "#app/lib/age-status";
import { ISSUE_AGE_STATUSES, useAppData } from "#app/lib/data";
import {
  useWorkspaceData,
  type WorkspaceDataState,
  type WorkspaceStage,
  type WorkspaceTeam,
  type WorkspaceUser,
  type WorkspaceWorkflow,
} from "#app/lib/data/workspace-data-provider";
import { resolveEntityLabel, type EntityData } from "#app/lib/entity-visuals";
import { UNASSIGNED_ASSIGNEE_ID } from "#app/lib/filters/build-where";
import { parseWorkflowStageValue } from "#app/lib/filters/composite-values";
import type {
  FilterFieldType,
  QuickFilterDef,
  UseFiltersResult,
} from "#app/lib/filters/types";
import { DATE_PRESETS, isPresetValue } from "#app/lib/utils/date-presets";
import { FILTER_MEMBER_STATUSES } from "#app/lib/role-utils";
import {
  useQuickFilterDirectory,
  type DirectoryKind,
  type QuickFilterDirectory,
} from "#app/lib/hooks/use-quick-filter-directory";

/**
 * ONE selectable value of ONE quick filter — a member, a team, a status, a
 * date preset — carrying its own read and write of the section's value.
 *
 * The panel has three consumers of the same set (the cross-section search,
 * the applied-value chips, the section's own control), so the set is defined
 * here once. A section that enumerated its values separately could disagree
 * with the search about what exists or with the chips about what is applied.
 */
export type QuickFilterOption = Omittable<{
  /** Unique within its section. */
  key: string;
  label: string;
  /** Extra match text — a member's email and username. Searched, never shown. */
  keywords?: string[];
  /** Set when the value IS an entity, so rows and chips render an EntityTag. */
  entity?: EntityData;
  /** Glyph for a value that is not an entity (a follow-up status, a preset). */
  icon?: IconName;
  iconColor?: IconColor;
  /** Sub-heading within the section: People/Teams, or a stage's workflow. */
  group?: string;
  /** How many rows picking this value would show, when the list knows —
   *  the member lists' facet counts. Rendered in the row's quiet caption ink
   *  ahead of the check; absent (or zero) draws nothing. */
  count?: number;
  selected: (value: unknown) => boolean;
  /** The section's next value with this option toggled. Null once it empties —
   *  `setQuickValue`'s contract for "no longer filtering". */
  toggle: (value: unknown) => unknown;
}>;

/** A quick filter and everything it can be set to. */
export type QuickFilterSection = Omittable<{
  def: QuickFilterDef;
  options: QuickFilterOption[];
  /** For a member or team pool: whether the server has confirmed an id is
   *  gone. The options stop at what is loaded, so an id outside them is
   *  retired only on this word (see `pruneUnknownIds`). */
  missing?: (id: string) => boolean;
  /** For the Assignee pool on a team screen: the team's roster, which the
   *  picker lists in a section of its own. */
  roster?: AssigneeRoster;
}>;

// ── Matching ────────────────────────────────────────────────────────────────

/** Below this many options a search field is chrome over a list the eye
 *  reads whole. */
export const SEARCH_THRESHOLD = 8;

/** Matches a cross-section search shows per section before the rest fold
 *  behind a count. */
export const QUICK_SEARCH_MATCH_CAP = 5;

/**
 * Whether an option answers a search. Shared by the section's own list and the
 * panel's cross-section search, so a member found by email in one is found by
 * email in the other. `query` is already trimmed and lower-cased.
 */
export function matchesQuery(option: QuickFilterOption, query: string): boolean {
  if (option.label.toLowerCase().includes(query)) return true;
  return !!option.keywords?.some((k) => k.toLowerCase().includes(query));
}

/**
 * What a section reads while nothing is applied. A filter's resting state is a
 * fact about the view — "Anyone", not an empty slot — so the row states it
 * where an applied value would sit.
 */
export function unfilteredFace(type: FilterFieldType): string {
  switch (type) {
    case "entity-user":
    case "entity-assignee":
      return "Anyone";
    case "entity-team":
      return "Any team";
    case "issue-type":
      return "Any type";
    case "workflow":
    case "workflow-stage":
      return "Any workflow";
    case "stage":
      return "Any stage";
    case "last-updated":
      return "Any time";
    case "age-status":
      return "Any status";
    default:
      return "Any";
  }
}

/** A run of options sharing one `group`. */
export type QuickOptionGroup = {
  key: string;
  /** Absent for the leading ungrouped run. */
  label: string | undefined;
  options: QuickFilterOption[];
};

/**
 * Options collected under their `group`, in FIRST-APPEARANCE order, so a group
 * never jumps ahead of one the index emitted before it. In workflow/stage that
 * puts the workflows above the per-workflow stage runs, which is the order the
 * index writes them in.
 */
export function groupQuickOptions(
  options: QuickFilterOption[],
): QuickOptionGroup[] {
  const groups: QuickOptionGroup[] = [];
  const byKey = new Map<string, QuickOptionGroup>();
  for (const option of options) {
    const key = option.group ?? "";
    let group = byKey.get(key);
    if (!group) {
      group = { key, label: option.group, options: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.options.push(option);
  }
  return groups;
}

// ── Value algebra ───────────────────────────────────────────────────────────

/** The `string[]` values (`in` / `hasSome`). `null` is a member in its own
 *  right: the follow-up filter stores updates-off as one. */
function idList(value: unknown): (string | null)[] {
  return Array.isArray(value) ? (value as (string | null)[]) : [];
}

function memberSelected(id: string | null) {
  return (value: unknown) => idList(value).includes(id);
}

function memberToggle(id: string | null) {
  return (value: unknown) => {
    const ids = idList(value);
    const next = ids.includes(id) ? ids.filter((v) => v !== id) : [...ids, id];
    return next.length > 0 ? next : null;
  };
}

function entityOption(
  id: string,
  entity: EntityData,
  extra?: Pick<QuickFilterOption, "group" | "keywords">,
): QuickFilterOption {
  return {
    key: id,
    label: resolveEntityLabel(entity),
    entity,
    ...extra,
    selected: memberSelected(id),
    toggle: memberToggle(id),
  };
}

// ── Entity pools as options ─────────────────────────────────────────────────

function userEntity(user: WorkspaceUser): EntityData {
  return { kind: "user", id: user.userId, user };
}

function teamEntity(team: WorkspaceTeam): EntityData {
  return { kind: "team", id: team.teamId, team };
}

function stageEntity(stage: WorkspaceStage): EntityData {
  return {
    kind: "stage",
    id: stage.stageId,
    stage,
    workflowId: stage.workflowId ?? undefined,
  };
}

function userKeywords(user: WorkspaceUser): string[] {
  return [user.email, user.username].filter((v): v is string => !!v);
}

function filterableUsers(users: WorkspaceUser[]): WorkspaceUser[] {
  return users.filter((u) => FILTER_MEMBER_STATUSES.includes(String(u.memberStatus)));
}

/**
 * A team's roster, for the screens that have one: the Assignee pool then
 * splits into this team's members and everyone else, because on a team's
 * own screen the team is who the reader means.
 */
export type AssigneeRoster = {
  memberIds: ReadonlySet<string>;
};

/** The headings over a split pool: the roster is named for what it IS on
 *  the screen (the team is in the masthead already), the rest for what it is
 *  not. */
export const TEAM_MEMBERS_GROUP = "Team members";
export const OTHER_MEMBERS_GROUP = "Other members";

/**
 * "Unassigned", as a value of the Assignee filter: the row for the work nobody
 * has. It leads the list and sits in no group — it answers a different
 * question from any name under it, and finding the unowned work is the
 * reason the row exists.
 */
const UNASSIGNED_OPTION: QuickFilterOption = {
  key: UNASSIGNED_ASSIGNEE_ID,
  label: "Unassigned",
  icon: "circle-dashed",
  selected: memberSelected(UNASSIGNED_ASSIGNEE_ID),
  toggle: memberToggle(UNASSIGNED_ASSIGNEE_ID),
};

/** The run with the viewer moved to its head — inside the run, never in a
 *  band above it: the rule every user picker follows for "me". */
function viewerFirst(users: WorkspaceUser[], viewerId: string | undefined): WorkspaceUser[] {
  if (!viewerId) return users;
  const index = users.findIndex((u) => u.userId === viewerId);
  if (index <= 0) return users;
  const viewer = users[index];
  if (!viewer) return users;
  return [viewer, ...users.slice(0, index), ...users.slice(index + 1)];
}

/**
 * The member pool as options. The viewer leads their run, because the most
 * common pick is oneself and a paged alphabetical roster puts them anywhere.
 * With a roster the pool splits into the team's members and the rest, each
 * under its own heading; without one it is a flat list.
 */
function memberOptions(
  users: WorkspaceUser[],
  viewerId: string | undefined,
  roster: AssigneeRoster | undefined,
  directory: QuickFilterDirectory | undefined,
): QuickFilterOption[] {
  const toOption = (user: WorkspaceUser, group?: string) =>
    entityOption(user.userId, userEntity(user), {
      keywords: userKeywords(user),
      group,
    });
  const active = filterableUsers(users);
  if (!roster) return viewerFirst(active, viewerId).map((u) => toOption(u));

  const inRoster = active.filter((u) => roster.memberIds.has(u.userId));
  const outside = active.filter((u) => !roster.memberIds.has(u.userId));
  // The roster is the team's whole membership, the bootstrap page is not:
  // a team member past it comes from the directory, into their own group.
  const listed = new Set(users.map((u) => u.userId));
  const rosterRest = directory
    ? [...roster.memberIds].flatMap((id) => {
        if (listed.has(id)) return [];
        const entity = directory.entityOf(id, "user");
        return entity?.kind === "user" &&
          FILTER_MEMBER_STATUSES.includes(String(entity.user?.memberStatus))
          ? [entityOption(id, entity, { keywords: entityKeywords(entity), group: TEAM_MEMBERS_GROUP })]
          : [];
      })
    : [];
  return [
    ...viewerFirst(inRoster, viewerId).map((u) => toOption(u, TEAM_MEMBERS_GROUP)),
    ...rosterRest,
    ...viewerFirst(outside, viewerId).map((u) => toOption(u, OTHER_MEMBERS_GROUP)),
  ];
}

function entityKeywords(entity: EntityData): string[] | undefined {
  if (entity.kind === "user") {
    return [entity.user?.email, entity.user?.username].filter((v): v is string => !!v);
  }
  if (entity.kind === "team") return entity.team.teamKey ? [entity.team.teamKey] : undefined;
  return undefined;
}

/** The directory kind a member or team pool holds, or undefined for any
 *  other pool. */
export function directoryKind(type: FilterFieldType): DirectoryKind | undefined {
  if (type === "entity-user" || type === "entity-assignee") return "user";
  if (type === "entity-team") return "team";
  return undefined;
}

/** A member or team past the loaded page, as an option of `type`'s pool —
 *  grouped with the other members when the pool is split by a roster. */
function directoryOption(
  entity: EntityData,
  type: FilterFieldType,
  roster: AssigneeRoster | undefined,
): QuickFilterOption {
  const group =
    type === "entity-assignee" && roster
      ? roster.memberIds.has(entity.id)
        ? TEAM_MEMBERS_GROUP
        : OTHER_MEMBERS_GROUP
      : undefined;
  return entityOption(entity.id, entity, { keywords: entityKeywords(entity), group });
}

/**
 * `options` plus each applied id they do not hold, resolved through the
 * directory — a member or team past the bootstrap page that a saved view
 * names. Without it the value would read as nobody and could not be
 * unchecked. An id still in flight, or gone, adds nothing.
 */
function withAppliedOptions(
  options: QuickFilterOption[],
  type: FilterFieldType,
  value: unknown,
  pools: QuickFilterPools,
): QuickFilterOption[] {
  const kind = directoryKind(type);
  const { directory, roster } = pools;
  if (!kind || !directory) return options;
  const known = new Set(options.map((option) => option.key));
  const extra: QuickFilterOption[] = [];
  for (const id of idList(value)) {
    if (typeof id !== "string" || known.has(id)) continue;
    const entity = directory.entityOf(id, kind);
    if (!entity) continue;
    known.add(id);
    extra.push(directoryOption(entity, type, roster));
  }
  return extra.length > 0 ? [...options, ...extra] : options;
}

/**
 * The options of `type`'s pool that answer `query` (trimmed, lower-cased):
 * the local matches, then each server match the loaded page does not hold.
 * Server matches go through the same `matchesQuery`, so a stale answer from
 * the previous keystroke drops out on its own.
 */
export function searchOptions(
  options: QuickFilterOption[],
  type: FilterFieldType | undefined,
  query: string,
  server: { members: EntityData[]; teams: EntityData[] },
): QuickFilterOption[] {
  const local = options.filter((option) => matchesQuery(option, query));
  const kind = type ? directoryKind(type) : undefined;
  if (!kind) return local;
  const known = new Set(options.map((option) => option.key));
  const split = options.some((option) => option.group);
  const hits = (kind === "user" ? server.members : server.teams).flatMap((entity) => {
    if (known.has(entity.id)) return [];
    const option = entityOption(entity.id, entity, {
      keywords: entityKeywords(entity),
      group: kind === "user" && split ? OTHER_MEMBERS_GROUP : undefined,
    });
    return matchesQuery(option, query) ? [option] : [];
  });
  return hits.length > 0 ? [...local, ...hits] : local;
}

/** Which server searches a set of pools needs. */
export function searchKinds(types: readonly FilterFieldType[]): {
  members: boolean;
  teams: boolean;
} {
  return {
    members: types.some((type) => directoryKind(type) === "user"),
    teams: types.some((type) => directoryKind(type) === "team"),
  };
}

// ── The workflow/stage composite ────────────────────────────────────────────

/**
 * Workflows and their stages share ONE value (`{workflowIds, stageIds}`), and
 * its halves nest rather than sit parallel: they decompose into ANDed
 * conditions, so a stage is only ever asked about within its own workflow.
 * Hence the cascade — picking a stage brings its workflow along, and dropping a
 * workflow drops the stages that hung off it.
 */
function workflowStageOptions(
  workflows: WorkspaceWorkflow[],
  stagesByWorkflow: Map<string, WorkspaceStage[]>,
  scopedWorkflowId: string | undefined,
): QuickFilterOption[] {
  const options: QuickFilterOption[] = [];

  // Scoped to one workflow: its stages, flat and stage-only. No workflow rows
  // (the screen already fixed it) and no workflow id written into the value —
  // a workflowIds member the panel no longer renders would be an applied
  // filter with nothing on screen to clear it.
  if (scopedWorkflowId) {
    for (const stage of stagesByWorkflow.get(scopedWorkflowId) ?? []) {
      const stageId = stage.stageId;
      options.push({
        key: `stage:${stageId}`,
        label: stage.name ?? "",
        entity: stageEntity(stage),
        selected: (value) =>
          parseWorkflowStageValue(value).stageIds.includes(stageId),
        toggle: (value) => {
          const { stageIds } = parseWorkflowStageValue(value);
          const next = stageIds.includes(stageId)
            ? stageIds.filter((id) => id !== stageId)
            : [...stageIds, stageId];
          return next.length > 0 ? { workflowIds: [], stageIds: next } : null;
        },
      });
    }
    return options;
  }

  for (const workflow of workflows) {
    const workflowId = workflow.workflowId;
    const stages = stagesByWorkflow.get(workflowId) ?? [];
    const entity: EntityData = { kind: "workflow", id: workflowId, workflow };

    options.push({
      key: `workflow:${workflowId}`,
      label: resolveEntityLabel(entity),
      entity,
      selected: (value) =>
        parseWorkflowStageValue(value).workflowIds.includes(workflowId),
      toggle: (value) => {
        const { workflowIds, stageIds } = parseWorkflowStageValue(value);
        if (workflowIds.includes(workflowId)) {
          const dropped = new Set(stages.map((s) => s.stageId));
          const nextWorkflows = workflowIds.filter((id) => id !== workflowId);
          const nextStages = stageIds.filter((id) => !dropped.has(id));
          return nextWorkflows.length > 0 || nextStages.length > 0
            ? { workflowIds: nextWorkflows, stageIds: nextStages }
            : null;
        }
        return { workflowIds: [...workflowIds, workflowId], stageIds };
      },
    });

    const workflowName = resolveEntityLabel(entity);
    for (const stage of stages) {
      const stageId = stage.stageId;
      options.push({
        key: `stage:${stageId}`,
        label: stage.name ?? "",
        entity: stageEntity(stage),
        group: workflowName,
        selected: (value) =>
          parseWorkflowStageValue(value).stageIds.includes(stageId),
        toggle: (value) => {
          const { workflowIds, stageIds } = parseWorkflowStageValue(value);
          if (stageIds.includes(stageId)) {
            const nextStages = stageIds.filter((id) => id !== stageId);
            return workflowIds.length > 0 || nextStages.length > 0
              ? { workflowIds, stageIds: nextStages }
              : null;
          }
          return {
            // A stage answers "which step of THIS workflow", so its workflow
            // joins the value with it — picked from the search, the stage
            // would otherwise sit in a value nothing on screen accounts for.
            workflowIds: workflowIds.includes(workflowId)
              ? workflowIds
              : [...workflowIds, workflowId],
            stageIds: [...stageIds, stageId],
          };
        },
      });
    }
  }

  return options;
}

// ── Static enums ────────────────────────────────────────────────────────────

/**
 * The follow-up states, as filter values. `disabled` is the client-only
 * updates-off sentinel and stores as `null` in the value array — the server
 * reports it as an absent `age`, never as a fourth status.
 */
export const AGE_STATUS_QUICK_OPTIONS: QuickFilterOption[] = [
  ...ISSUE_AGE_STATUSES.map((key) => {
    const config = AGE_STATUS_CONFIG[key];
    return {
      key,
      label: config.label,
      // The glyph and palette hue every update clock paints (see `AgeLabel`),
      // so a chip matches the cells it filters.
      icon: config.icon,
      iconColor: config.decoColor,
      selected: memberSelected(key),
      toggle: memberToggle(key),
    };
  }),
  {
    key: "disabled",
    label: AGE_STATUS_CONFIG.disabled.label,
    icon: "circle-paused",
    iconColor: AGE_STATUS_CONFIG.disabled.decoColor,
    selected: memberSelected(null),
    toggle: memberToggle(null),
  },
];

/** The "More" section: one flag, stored as `true`/absent. */
export const ARCHIVED_QUICK_OPTIONS: QuickFilterOption[] = [
  {
    key: "archived",
    label: "Archived only",
    icon: "archive",
    selected: (value) => value === true,
    toggle: (value) => (value === true ? null : true),
  },
];

/**
 * The date presets, single-select — picking one replaces the last, picking the
 * applied one clears. Two keys are not values and stay out: "Custom Date Range"
 * reveals the control's own start/end pickers, and "All Time" IS the unset
 * state (see `LastUpdatedInput`), so neither is something to search for.
 */
export const LAST_UPDATED_QUICK_OPTIONS: QuickFilterOption[] = DATE_PRESETS
  .filter((preset) => preset.key !== "custom" && preset.key !== "all-time")
  .map((preset) => ({
    key: preset.key,
    label: preset.label,
    icon: "calendar-clock" as IconName,
    selected: (value: unknown) =>
      isPresetValue(value) && value.key === preset.key,
    toggle: (value: unknown) =>
      isPresetValue(value) && value.key === preset.key
        ? null
        : { key: preset.key },
  }));

// ── The index ───────────────────────────────────────────────────────────────

/** The workspace pools every enumerable filter value comes from, plus what
 *  orders the member pool: the viewer (leads their run) and a team roster
 *  (splits the pool). Both optional — a summary that only needs names
 *  passes the workspace state as is. */
export type QuickFilterPools = Pick<
  WorkspaceDataState,
  | "users"
  | "teams"
  | "issueTypes"
  | "workflows"
  | "stages"
  | "stagesByWorkflow"
  | "workflowMap"
> & {
  viewerId?: string | undefined;
  roster?: AssigneeRoster | undefined;
  /** Members and teams past the bootstrap pages — an applied one joins the
   *  options, and a team's whole roster joins the Assignee pool. */
  directory?: QuickFilterDirectory | undefined;
};

/**
 * Every value one quick-filter type can take.
 *
 * Types with no enumerable pool (free text, a number, a role) come back empty —
 * their sections still render their own control, they just have nothing for the
 * panel's search to offer.
 */
export function buildQuickFilterOptions(
  type: FilterFieldType,
  pools: QuickFilterPools,
  scopedWorkflowId?: string,
  value?: unknown,
): QuickFilterOption[] {
  return withAppliedOptions(
    poolOptions(type, pools, scopedWorkflowId),
    type,
    value,
    pools,
  );
}

function poolOptions(
  type: FilterFieldType,
  pools: QuickFilterPools,
  scopedWorkflowId: string | undefined,
): QuickFilterOption[] {
  const {
    users,
    teams,
    issueTypes,
    workflows,
    stages,
    stagesByWorkflow,
    workflowMap,
    viewerId,
    roster,
    directory,
  } = pools;

  switch (type) {
    case "entity-user":
      return memberOptions(users, viewerId, undefined, directory);

    // The people doing the work, led by the row for work nobody is doing.
    case "entity-assignee":
      return [UNASSIGNED_OPTION, ...memberOptions(users, viewerId, roster, directory)];

    case "entity-team":
      return teams.map((team) => entityOption(team.teamId, teamEntity(team)));

    case "issue-type":
      return issueTypes.map((issueType) =>
        entityOption(issueType.issueTypeId, {
          kind: "issueType",
          id: issueType.issueTypeId,
          issueType,
        }),
      );

    case "workflow":
      return workflows.map((workflow) =>
        entityOption(workflow.workflowId, {
          kind: "workflow",
          id: workflow.workflowId,
          workflow,
        }),
      );

    case "stage":
      return stages.map((stage) =>
        entityOption(stage.stageId, stageEntity(stage), {
          group: stage.workflowId
            ? (workflowMap.get(stage.workflowId)?.name ?? undefined)
            : undefined,
        }),
      );

    case "workflow-stage":
      return workflowStageOptions(workflows, stagesByWorkflow, scopedWorkflowId);

    case "age-status":
      return AGE_STATUS_QUICK_OPTIONS;

    case "last-updated":
      return LAST_UPDATED_QUICK_OPTIONS;

    case "more-options":
      return ARCHIVED_QUICK_OPTIONS;

    default:
      return [];
  }
}

/**
 * Every quick filter in a config paired with every value it can take.
 *
 * One hook for the whole panel: it reads the workspace pools once, so the
 * cross-section search costs nothing per section.
 */
export function useQuickFilterOptions(
  defs: readonly QuickFilterDef[],
  scopedWorkflowId?: string,
  roster?: AssigneeRoster,
  valueOf?: (field: string) => unknown,
): QuickFilterSection[] {
  const workspace = useWorkspaceData();
  const { currentUser } = useAppData();
  const viewerId = currentUser?.userId;

  // Keyed by content: the applied values decide which ids past the loaded
  // pages join the options, and a layer hands out a fresh `valueOf` per render.
  const valuesKey = JSON.stringify(defs.map((def) => valueOf?.(def.field) ?? null));
  const values = useMemo(() => JSON.parse(valuesKey) as unknown[], [valuesKey]);
  const appliedTeamIds = useMemo(
    () =>
      defs.flatMap((def, index) =>
        directoryKind(def.type) === "team"
          ? idList(values[index]).filter((id): id is string => typeof id === "string")
          : [],
      ),
    [defs, values],
  );
  const directory = useQuickFilterDirectory(appliedTeamIds);

  const pools = useMemo<QuickFilterPools>(
    () => ({ ...workspace, viewerId, roster, directory }),
    [workspace, viewerId, roster, directory],
  );

  return useMemo(
    () =>
      defs.map((def, index) => {
        const kind = directoryKind(def.type);
        return {
          def,
          options: buildQuickFilterOptions(def.type, pools, scopedWorkflowId, values[index]),
          missing: kind ? (id: string) => directory.missingOf(id, kind) : undefined,
          roster: def.type === "entity-assignee" ? roster : undefined,
        };
      }),
    [defs, pools, scopedWorkflowId, values, directory, roster],
  );
}

/** The types whose value is a list of entity ids the pool can vouch for. */
const ID_POOL_TYPES: ReadonlySet<FilterFieldType> = new Set([
  "entity-user",
  "entity-assignee",
  "entity-team",
]);

/**
 * The next value with the ids the section can no longer name dropped — a
 * member removed since a view saved them, a team deleted. Only the id-list
 * pools: composites and enums (whose "off" member is `null`) pass through.
 *
 * Pruned on WRITE, never on read: a stale id keeps narrowing the query and
 * lighting the chip until the reader touches the filter, and the first edit
 * they make is what retires it — so a saved view never silently changes
 * under them, and never keeps a ghost either.
 */
export function pruneUnknownIds(section: QuickFilterSection, next: unknown): unknown {
  if (!ID_POOL_TYPES.has(section.def.type) || !Array.isArray(next)) return next;
  const known = new Set(section.options.map((option) => option.key));
  // A member or team pool stops at its loaded page, so an id outside it is
  // retired only once the server confirms it is gone — never because it is
  // past the page or still being looked up.
  const { missing } = section;
  if (missing) {
    const kept = (next as unknown[]).filter(
      (id) => typeof id === "string" && (known.has(id) || !missing(id)),
    );
    return kept.length > 0 ? kept : null;
  }
  // An empty pool can vouch for nothing — keep the value rather than
  // clearing a filter over a roster that has not loaded. Only entity rows
  // count: the Assignee pool always carries its Unassigned row.
  if (!section.options.some((option) => option.entity)) return next;
  const kept = (next as unknown[]).filter(
    (id) => typeof id === "string" && known.has(id),
  );
  return kept.length > 0 ? kept : null;
}

/** ONE write path for a section's value: the layer's setter, through the
 *  prune above. Every door (a row, a search hit, a toolbar chip) writes
 *  through here so they retire stale ids alike. */
export function writeQuickValue(
  layer: Pick<UseFiltersResult, "setQuickValue">,
  section: QuickFilterSection,
  next: unknown,
): void {
  layer.setQuickValue(section.def.field, pruneUnknownIds(section, next));
}

/** Every applied option across the panel, in section order. */
export function selectedOptions(
  sections: QuickFilterSection[],
  quickValues: Record<string, unknown>,
): { section: QuickFilterSection; option: QuickFilterOption }[] {
  return sections.flatMap((section) =>
    section.options
      .filter((option) => option.selected(quickValues[section.def.field]))
      .map((option) => ({ section, option })),
  );
}
